import { symphonyAbi, symphonyAddress } from "../../common/constants/contract";
import { ethers } from "ethers-v5";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { getConfig } from "../constants/config";
import { calculateAmountOutMin } from "../helpers/calculateAmountOutMin";
import { verifySigner } from "../helpers/verifySigner";
import { giveApproval } from "./giveApproval";
import { checkApproval } from "./checkApproval";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTokenOut } from "../helpers/getTokenOut";

/**
 * Executes a swap transaction on the Symphony protocol.
 * Handles native token, ERC20 token, and cross-token swaps with optional approval checks.
 *
 * @param {Object} params - Swap parameters
 * @param {Array} params.route - Route information from getRoute
 * @param {boolean} params.includesNative - Whether the swap includes native token
 * @param {ethers.Signer} [params.signer] - Signer for the transaction
 * @param {Object} [params.options] - Swap options
 * @param {boolean} [params.options.skipApproval=true] - Skip token approval step
 * @param {boolean} [params.options.skipCheckApproval=false] - Skip approval check
 * @param {Object} [params.slippage] - Slippage configuration
 * @param {number} [params.slippage.slippageAmount=config.slippage] - Slippage amount
 * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
 *
 * @returns {Promise<Object>} Transaction response object
 *
 * @throws {Error} If signer verification fails
 * @throws {Error} If approval check/transaction fails
 * @throws {Error} If swap transaction fails
 *
 * @example
 * // Example 1: Native SEI to USDC swap
 * const route = await getRoute({
 *   tokenIn: "0x0", // SEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const tx = await swap({
 *   route,
 *   includesNative: true,
 *   signer: provider.getSigner(),
 *   options: {
 *     skipApproval: true, // No approval needed for native token
 *     wait: 3 // Wait for 3 confirmations
 *   }
 * });
 *
 * @example
 * // Example 2: Token to token swap with custom slippage
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xdAC17F958D2ee523a2206206994597C13D831ec7", // USDT
 *   amount: "1.0"
 * });
 * const tx = await swap({
 *   route,
 *   includesNative: false,
 *   signer: provider.getSigner(),
 *   options: {
 *     skipApproval: false, // Check and perform approval
 *     skipCheckApproval: false
 *   },
 *   slippage: {
 *     slippageAmount: 50, // 0.5%
 *     isBps: true
 *   }
 * });
 */
export const swap = async ({
  route,
  includesNative,
  signer = undefined,
  options = {
    skipApproval: true,
    skipCheckApproval: false,
    wait: 1,
  },
  slippage = {
    slippageAmount: getConfig().slippage,
    isBps: false,
  },
}) => {
  try {
    const {
      provider,
      nativeAddress,
      wrappedNativeAddress,
      functionName,
      feeParams,
    } = getConfig();
    let tokenIn = getTokenIn(route);
    let tokenOut = getTokenOut(route);
    let isTokenApproved = options.skipCheckApproval;
    let approvalReceipt = undefined;

    // If native currency included, treat wrapped currency as native
    includesNative
      ? tokenIn === wrappedNativeAddress
        ? (tokenIn = nativeAddress)
        : (tokenOut = nativeAddress)
      : null;

    // Verify signer
    const verifiedSigner = await verifySigner(signer);

    // Check approval
    if (!options.skipCheckApproval) {
      const approvalCheck = await checkApproval({
        variable: route,
        signer: verifiedSigner,
        includesNative,
      });
      isTokenApproved = approvalCheck;
    }

    // Give approval
    if (!options.skipApproval && !isTokenApproved) {
      const approve = await giveApproval({
        variable: route,
        signer: verifiedSigner,
      });
      approvalReceipt = approve;
    }

    // Create contract instance
    const contract = new ethers.Contract(
      symphonyAddress,
      symphonyAbi,
      verifiedSigner
    );
    const amountOutMin = await calculateAmountOutMin({
      variable: route,
      slippageAmount: slippage.slippageAmount,
      options: {
        isBps: slippage.isBps ?? false,
      },
    });

    // NATIVE TO TOKEN TRANSACTION
    if (tokenIn === nativeAddress && tokenOut !== nativeAddress) {
      // Simulate the transaction before sending
      await contract.callStatic[functionName](
        route,
        amountOutMin,
        false,
        [
          feeParams.paramFee,
          feeParams.feeAddress,
          feeParams.feeSharePercentage,
        ],
        {
          value: (await getTotalAmountIn(route)).amountIn.toString(),
        }
      );

      // Send the actual transaction
      const receipt = await contract[functionName](
        route,
        amountOutMin,
        false,
        [
          feeParams.paramFee,
          feeParams.feeAddress,
          feeParams.feeSharePercentage,
        ],
        {
          value: (await getTotalAmountIn(route)).amountIn.toString(),
        }
      );
      const fullReceipt = await receipt.wait(options.wait);
      return {
        swapReceipt: fullReceipt,
        approveReceipt: approvalReceipt,
      };
    }

    // TOKEN TO NATIVE TRANSACTION
    if (tokenIn !== wrappedNativeAddress && tokenOut === nativeAddress) {
      // Simulate the transaction before sending
      await contract.callStatic[functionName](route, amountOutMin, true, [
        feeParams.paramFee,
        feeParams.feeAddress,
        feeParams.feeSharePercentage,
      ]);

      // Send the actual transaction
      const receipt = await contract[functionName](route, amountOutMin, true, [
        feeParams.paramFee,
        feeParams.feeAddress,
        feeParams.feeSharePercentage,
      ]);
      const fullReceipt = await receipt.wait(options.wait);
      return {
        swapReceipt: fullReceipt,
        approveReceipt: approvalReceipt,
      };
    }

    // TOKEN TO TOKEN TRANSACTION
    if (tokenIn !== nativeAddress && tokenOut !== nativeAddress) {
      // Simulate the transaction before sending
      await contract.callStatic[functionName](route, amountOutMin, false, [
        feeParams.paramFee,
        feeParams.feeAddress,
        feeParams.feeSharePercentage,
      ]);

      // Send the actual transaction
      const receipt = await contract[functionName](route, amountOutMin, false, [
        feeParams.paramFee,
        feeParams.feeAddress,
        feeParams.feeSharePercentage,
      ]);
      const fullReceipt = await receipt.wait(options.wait);
      return {
        swapReceipt: fullReceipt,
        approveReceipt: approvalReceipt,
      };
    }
  } catch (error) {
    console.error("Swap failed:", error);
    throw error;
  }
};
